import React, { useState, useEffect, useRef } from 'react';
import { Header, Loader, Button } from 'semantic-ui-react';
import { getDashboardChart } from '~/api/admin';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { CHART_COLORS, DEPLOYMENT_TYPE_COLORS } from './chartColors';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend
);

const IRR_TREND_LINE_COLORS: Record<string, string> = {
  Overall: CHART_COLORS.softBlue,
  CI: DEPLOYMENT_TYPE_COLORS.CI ?? CHART_COLORS.softBlue,
  Community: DEPLOYMENT_TYPE_COLORS.Community ?? DEPLOYMENT_TYPE_COLORS.COMMUNITY_SOLAR ?? CHART_COLORS.mutedGreen,
  Utility: DEPLOYMENT_TYPE_COLORS.Utility ?? DEPLOYMENT_TYPE_COLORS.UTILITY ?? CHART_COLORS.softTerracotta,
};

interface ChartData {
  labels: string[];
  datasets?: { label: string; data: (number | null)[] }[];
  /** @deprecated Use datasets instead - backward compat for old API */
  values?: number[];
}



type BidsFilter = 'all' | 'accepted';

interface MedianIrrTrendChartProps {
  interval?: string;
  onIntervalChange?: (interval: string) => void;
  states?: string[];
}

const MedianIrrTrendChart: React.FC<MedianIrrTrendChartProps> = ({
  interval: intervalProp = 'all',
  states = [],
}) => {
  const [data, setData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [bidsFilter, setBidsFilter] = useState<BidsFilter>('all');
  const interval = intervalProp;
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<ChartJS | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params: Record<string, string> = { interval, bids: bidsFilter };
        if (states.length) params.states = states.join(',');
        const result = await getDashboardChart('median-irr-trend', params);
        if (!result) {
          setData(null);
          return;
        }
        // Normalize: support old API format { labels, values } for backward compat
        const normalized: ChartData =
          result.datasets != null
            ? result
            : {
                labels: result.labels ?? [],
                datasets: [
                  {
                    label: 'Overall',
                    data: (result.values ?? []).map((v: number) => v),
                  },
                ],
              };
        setData(normalized);
      } catch (error) {
        console.error('Error fetching median IRR trend chart data:', error);
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [interval, states, bidsFilter]);

  useEffect(() => {
    if (!loading && data?.labels?.length && data?.datasets?.length && chartRef.current) {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      const ctx = chartRef.current.getContext('2d');
      if (ctx) {
        chartInstance.current = new ChartJS(ctx, {
          type: 'line',
          data: {
            labels: data.labels,
            datasets: data.datasets
              .filter((ds) => ds.label !== 'Overall')
              .map((ds) => {
              const color = IRR_TREND_LINE_COLORS[ds.label] ?? CHART_COLORS.softGray;
              return {
                label: `${ds.label} median IRR (%)`,
                data: ds.data,
                borderColor: color,
                backgroundColor: `${color}20`,
                fill: false,
                tension: 0.3,
                pointRadius: 4,
                pointHoverRadius: 6,
                spanGaps: false,
              };
            }),
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'top',
              },
              title: {
                display: false,
              },
              tooltip: {
                callbacks: {
                  label: (context) =>
                    context.parsed.y != null
                      ? `${(context.parsed.y * 100).toFixed(2)}%`
                      : '',
                },
              },
            },
            scales: {
              x: {
                title: {
                  display: true,
                  text: 'Month',
                },
              },
              y: {
                title: {
                  display: true,
                  text: 'IRR',
                },
                beginAtZero: true,
                ticks: {
                  callback: (value) =>
                    typeof value === 'number' ? value.toFixed(2) : value,
                },
              },
            },
          },
        });
      }

      return () => {
        if (chartInstance.current) {
          chartInstance.current.destroy();
        }
      };
    }
  }, [data, loading]);

  if (loading) {
    return (
      <div className="card">
        <Header as="h2">Median IRR Trend</Header>
        <Loader active inline="centered" />
      </div>
    );
  }

  const visibleDatasets = data?.datasets?.filter((ds) => ds.label !== 'Overall') ?? [];
  const hasData =
    data?.labels?.length &&
    visibleDatasets.some((ds) => ds.data.some((v) => v != null));

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <Header as="h2" style={{ margin: 0 }}>Median IRR Trend</Header>
        <Button.Group>
          <Button
            active={bidsFilter === 'all'}
            onClick={() => setBidsFilter('all')}
          >
            All bids
          </Button>
          <Button
            active={bidsFilter === 'accepted'}
            onClick={() => setBidsFilter('accepted')}
          >
            Accepted only
          </Button>
        </Button.Group>
      </div>
      {hasData ? (
        <div style={{ height: '400px', width: '100%' }}>
          <canvas ref={chartRef} />
        </div>
      ) : (
        <p>No data available</p>
      )}
    </div>
  );
};

export default MedianIrrTrendChart;
